import { useEffect, useMemo, useState } from "react";
import type { Project } from "@shared/domain";
import { Button } from "@/components/ui/Button";
import { InputField } from "@/components/ui/InputField";
import { Sheet } from "@/components/ui/Sheet";
import { useUpdateProjectMutation } from "@/hooks/useBackendData";
import { useAppStore } from "@/state/appStore";

interface Props {
  open: boolean;
  onClose: () => void;
  projectId?: string;
}

const COLOR_OPTIONS = ["#6C3CF5", "#0DC5D6", "#E0A100", "#1E8E3E", "#D93025"];
const ICON_OPTIONS = ["folder", "rocket_launch", "analytics", "build", "campaign"];
const WORDPRESS_INTEGRATION_ID = "int_wordpress";

export const ProjectProfileSheet = ({ open, onClose, projectId }: Props): JSX.Element | null => {
  const project = useAppStore((state) => state.projects.find((item) => item.id === projectId));
  const goals = useAppStore((state) => state.goals);
  const members = useAppStore((state) => state.members);
  const agents = useAppStore((state) => state.agents);
  const integrations = useAppStore((state) => state.integrations);
  const updateProjectMutation = useUpdateProjectMutation();
  const connectedAgents = useMemo(() => agents.filter((agent) => agent.status === "active"), [agents]);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [icon, setIcon] = useState(ICON_OPTIONS[0]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [primaryGoalId, setPrimaryGoalId] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [defaultAgentId, setDefaultAgentId] = useState("");
  const [connectedToolIds, setConnectedToolIds] = useState<string[]>([]);
  const [recommendedAgentIds, setRecommendedAgentIds] = useState<string[]>([]);
  const [siteDispatchPolicy, setSiteDispatchPolicy] = useState<Project["siteDispatchPolicy"]>("plugin_first");

  useEffect(() => {
    if (!open || !project) return;
    setTitle(project.title);
    setDescription(project.description ?? "");
    setColor(project.color || COLOR_OPTIONS[0]);
    setIcon(project.icon || ICON_OPTIONS[0]);
    setSelectedGoalIds(project.goalIds ?? []);
    setPrimaryGoalId(project.primaryGoalId ?? "");
    setSelectedMemberIds(project.memberIds ?? []);
    setDefaultAgentId(project.defaultAgentId ?? "");
    setConnectedToolIds(project.connectedToolIds ?? []);
    setRecommendedAgentIds(project.recommendedAgentIds ?? []);
    setSiteDispatchPolicy(project.siteDispatchPolicy ?? "plugin_first");
  }, [open, project]);

  const canSubmit = useMemo(() => title.trim().length >= 2 && Boolean(project), [project, title]);

  if (!open || !project) return null;

  const toggleFromList = (value: string, list: string[], setList: (next: string[]) => void) => {
    if (list.includes(value)) {
      setList(list.filter((item) => item !== value));
      return;
    }
    setList([...list, value]);
  };

  const projectPatch: Partial<
    Pick<
      Project,
      "title" | "description" | "color" | "icon" | "goalIds" | "primaryGoalId" | "memberIds" | "defaultAgentId" | "connectedToolIds" | "recommendedAgentIds" | "siteDispatchPolicy"
    >
  > = {
    title: title.trim(),
    description: description.trim() || undefined,
    color,
    icon,
    goalIds: selectedGoalIds,
    primaryGoalId: primaryGoalId || undefined,
    memberIds: selectedMemberIds,
    defaultAgentId: defaultAgentId || undefined,
    connectedToolIds,
    recommendedAgentIds,
    siteDispatchPolicy: connectedToolIds.includes(WORDPRESS_INTEGRATION_ID) ? siteDispatchPolicy : undefined
  };

  return (
    <Sheet open={open} title="Project Profile" onClose={onClose} height="80">
      <div className="space-y-4">
        <InputField
          label="Title *"
          inputProps={{
            value: title,
            onChange: (event) => setTitle(event.target.value),
            placeholder: "Growth Sprint"
          }}
        />

        <InputField
          kind="textarea"
          label="Description / Context"
          textareaProps={{
            value: description,
            onChange: (event) => setDescription(event.target.value),
            placeholder: "Goals, constraints, business context, tool expectations"
          }}
        />

        <div>
          <p className="mb-2 text-sm font-medium">Color</p>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className="h-8 w-8 rounded-full border"
                style={{
                  background: option,
                  borderColor: color === option ? "var(--accent-primary)" : "var(--border-default)",
                  boxShadow: color === option ? "0 0 0 2px color-mix(in srgb, var(--accent-primary) 30%, transparent)" : "none"
                }}
                onClick={() => setColor(option)}
                aria-label={`Select ${option}`}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Icon</p>
          <div className="flex flex-wrap gap-2">
            {ICON_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className="rounded-lg border px-3 py-2 text-sm"
                style={{
                  borderColor: icon === option ? "var(--accent-primary)" : "var(--border-default)",
                  background: icon === option ? "var(--accent-primary-subtle)" : "var(--bg-tertiary)"
                }}
                onClick={() => setIcon(option)}
              >
                <span className="material-symbols-outlined text-base align-middle">{option}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Linked Goals</p>
          <div className="space-y-2">
            {goals.map((goal) => {
              const checked = selectedGoalIds.includes(goal.id);
              return (
                <label key={goal.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const next = checked ? selectedGoalIds.filter((id) => id !== goal.id) : [...selectedGoalIds, goal.id];
                      setSelectedGoalIds(next);
                      if (checked && primaryGoalId === goal.id) setPrimaryGoalId("");
                    }}
                  />
                  <span>{goal.title}</span>
                </label>
              );
            })}
          </div>
        </div>

        <label className="block text-sm text-[var(--text-secondary)]">
          Primary Goal
          <select className="input mt-1" value={primaryGoalId} onChange={(event) => setPrimaryGoalId(event.target.value)}>
            <option value="">None</option>
            {goals
              .filter((goal) => selectedGoalIds.includes(goal.id))
              .map((goal) => (
                <option key={goal.id} value={goal.id}>
                  {goal.title}
                </option>
              ))}
          </select>
        </label>

        <div>
          <p className="mb-2 text-sm font-medium">Members</p>
          <div className="space-y-2">
            {members.map((member) => {
              const checked = selectedMemberIds.includes(member.userId);
              return (
                <label key={member.userId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFromList(member.userId, selectedMemberIds, setSelectedMemberIds)}
                  />
                  <span>
                    {member.name} ({member.role})
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <label className="block text-sm text-[var(--text-secondary)]">
          Default Agent
          <select className="input mt-1" value={defaultAgentId} onChange={(event) => setDefaultAgentId(event.target.value)}>
            <option value="">None</option>
            {connectedAgents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
        </label>

        <div>
          <p className="mb-2 text-sm font-medium">Connected Tools</p>
          <div className="space-y-2">
            {integrations.map((integration) => {
              const checked = connectedToolIds.includes(integration.id);
              return (
                <label key={integration.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFromList(integration.id, connectedToolIds, setConnectedToolIds)}
                  />
                  <span>
                    {integration.name} ({integration.status})
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {connectedToolIds.includes(WORDPRESS_INTEGRATION_ID) ? (
          <label className="block text-sm text-[var(--text-secondary)]">
            WordPress Dispatch Policy
            <select
              className="input mt-1"
              value={siteDispatchPolicy ?? "plugin_first"}
              onChange={(event) => setSiteDispatchPolicy(event.target.value as Project["siteDispatchPolicy"])}
            >
              <option value="plugin_first">Plugin First (recommended)</option>
              <option value="plugin_only">Plugin Only</option>
              <option value="extension_only">Extension Only</option>
            </select>
          </label>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-medium">Recommended Agents</p>
          <div className="space-y-2">
            {connectedAgents.map((agent) => {
              const checked = recommendedAgentIds.includes(agent.id);
              return (
                <label key={agent.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFromList(agent.id, recommendedAgentIds, setRecommendedAgentIds)}
                  />
                  <span>{agent.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        <Button
          className="w-full"
          disabled={!canSubmit || updateProjectMutation.isPending}
          onClick={async () => {
            try {
              await updateProjectMutation.mutateAsync({ id: project.id, patch: projectPatch });
              onClose();
            } catch {
              // toast emitted in mutation
            }
          }}
        >
          {updateProjectMutation.isPending ? "Saving..." : "Save Project"}
        </Button>
      </div>
    </Sheet>
  );
};
